import { VersionRange } from '../../models/core/version-range.js';
import { DataModelBuilder,
  isSameData, isSameList, SerializationContext, SerializedData } from '../../models/dto/dto.js';
import { DateSpan } from '../../models/serializable/date-span.js';
import { MapUtils } from '../../utility/map-utils.js';
import { RenderableComponent } from '../core/component.js';

export class OccasionsComponent extends RenderableComponent {
  static readonly TYPE_ID: string = 'caa5fc10e26a43dfbf623b80584dfa98';
  static readonly SCHEMA_VERSION: number = 2;
  static readonly DEFAULT_RENDER_ORDER: number = 6;

  constructor(
        public readonly occasions: DateSpan[],
        public readonly renderOrder: number,
        public readonly isRenderingEnabled: boolean,
  ) {
    super();
  }

  get $typeId(): string {
    return OccasionsComponent.TYPE_ID;
  }

  get $removable(): boolean {
    return true;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, OccasionsComponent.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): OccasionsComponent {
    return new OccasionsComponent(
        MapUtils.getList<DateSpan>(map, 'occasions', (data) => context.fromData<DateSpan>(data as SerializedData)),
        MapUtils.get<number>(map, 'renderOrder'),
        MapUtils.get<boolean>(map, 'isRenderingEnabled'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'occasions': this.occasions.map((e) => context.toData(e)),
      'renderOrder': this.renderOrder,
      'isRenderingEnabled': this.isRenderingEnabled,
    };
  }

  clone(): this {
    return new OccasionsComponent(
        this.occasions.map((e) => e.clone()),
        this.renderOrder,
        this.isRenderingEnabled,
    ) as this;
  }

  isSame(other: OccasionsComponent): boolean {
    if(this === other) {
      return true;
    }

    return isSameList(other.occasions, this.occasions, (a, b) => isSameData(a, b)) &&
      other.renderOrder === this.renderOrder &&
      other.isRenderingEnabled === this.isRenderingEnabled;
  }
}

export class OccasionsComponentBuilder extends DataModelBuilder<OccasionsComponent> {
  build(context: SerializationContext, map: SerializedData): OccasionsComponent {
    return OccasionsComponent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(OccasionsComponent.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(OccasionsComponent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    if(version < 2) {
      MapUtils.setIfNullOrUndefined(map, 'renderOrder', OccasionsComponent.DEFAULT_RENDER_ORDER);
      MapUtils.setIfNullOrUndefined(map, 'isRenderingEnabled', true);
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
